<template>
  <tr>
    <td colspan="5">
      <b>{{ index }}. {{ name }}</b>
    </td>
  </tr>
  <template v-for="configuration in configurations" :key="configuration.id">
    <print-cloud-configuration
      :configuration="configuration"
      :article="article"
      :index="index"
      @show="show">
    </print-cloud-configuration>
  </template>
  <tr v-if="basketConfigurations.length > 0">
    <td colspan="5" style="font-weight: 400; padding-top: 32px;">
      {{ $t('configurator.labels.in_basket') }}
    </td>
  </tr>
  <template v-for="configuration,cIndex in basketConfigurations" :key="cIndex">
    <print-cloud-basket-configuration
      :configuration="configuration"
      :article="article"
      @remove="removeConfiguration"
      @show="show">
    </print-cloud-basket-configuration>
  </template>
  <tr v-if="availablePrints.length > 0 && isCloudStatusOpen && !isShopClosed">
    <td colspan="5">
      <span class="text-primary cursor--pointer" @click="showAddPrintModal">
        <i class="fa fa-plus mr-1"></i> {{ $t('configurator.buttons.add_print') }}
      </span>
    </td>
  </tr>
  <tr v-if="showOrderWarning">
    <td colspan="5">
      <span class="cursor--pointer" @click="showAddPrintModal">
        <i class="fa fa-warning mr-1 text-warning"></i> {{ $t('configurator.notices.warnings.no_print_articles_available') }}
      </span>
    </td>
  </tr>
  <tr v-if="missingPrints.length > 0 && isCloudStatusOpen && !isShopClosed">
    <template v-for="missingPrint in missingPrints" :key="missingPrint.id">
      <print-cloud-missing-print-warning :print="missingPrint">
      </print-cloud-missing-print-warning>
    </template>
  </tr>
  <template v-if="notBookablePrints.length > 0 && isCloudStatusOpen && !isShopClosed">
    <tr v-for="notBookablePrint in notBookablePrints" :key="notBookablePrint.id">
      <print-cloud-not-bookable-print-warning :print="notBookablePrint">
      </print-cloud-not-bookable-print-warning>
    </tr>
  </template>
  <modal
    :is-active="addPrintModalActive"
    @close="hideAddPrintModal"
    :title="$t('configurator.modals.titles.add_print')">
    <template #body>
      <div class="form-horizontal">
        <div class="form-group">
          <label for="print" class="control-label">{{ $t('configurator.form.labels.choose_surface') }}</label>
          <div class="row">
            <div class="col-sm-12">
              <select class="form-control" name="print" v-model="printForm.print">
                <option v-for="print,index in availablePrints" :value="print" :key="index">
                  {{ !inEnglish ? print.article_print.name : print.article_print.name_en }}
                </option>
              </select>
              <p v-if="printForm.errors.has('print')" class="text-danger">
                {{ $t('configurator.form.errors.graphic_design') }}
              </p>
            </div>
          </div>
        </div>
        <div class="form-group" v-if="showCount">
          <label>{{ $t('configurator.form.labels.choose_size') }}:</label>
          <div class="row">
            <div class="col-sm-12">
              <input
                class="form-control"
                type="number"
                :step="1"
                :min="1"
                :max="article.amount"
                :default="article.amount"
                name="count"
                v-model="printForm.count">
            </div>
          </div>
        </div>
        <div class="form-group">
          <label for="print_upload">{{ $t('configurator.form.labels.select_file') }}:</label>
          <drop-zone :id="dropzoneId" @drop.prevent="drop" @change="selectedFile"></drop-zone>
          <p class="help-block mb-1 pb-1" style="font-weight: 400;">
            {{ $t('configurator.form.labels.selected_file') }}:
            <span v-if="dropzoneFile">{{ dropzoneFile.name }}</span>
          </p>
          <printing-information></printing-information>
          <p v-if="printForm.errors.has('print_upload')"
             class="text-danger"
             v-html="$t('errors.print_upload')">
          </p>
        </div>
        <div class="form-group text-center">
          <button type="button" class="btn btn-warning mr-2" @click.prevent="hideAddPrintModal">
            {{ $t('configurator.buttons.cancel') }}
          </button>
          <button type="button" class="btn btn-primary" @click.prevent="addConfiguration">
            {{ $t('configurator.buttons.add') }}
          </button>
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup>
import {computed, reactive, ref} from 'vue'
import {useMainStore} from '@/store'
import {useI18n} from 'vue-i18n'
import {PRINT_CLOUD_STATUS_OPEN} from '@/constants'
import Form from '@/classes/Form'
import FormDataForm from '@/classes/FormDataForm'
import {hideLoadingIndicator, showErrorToast, showLoadingIndicator, showWarningToast} from '@/utils/toast'
import { useCloudStore } from '@/stores/cloud'

const {t} = useI18n({useScope: 'global'})
const store = useMainStore()
const cloudStore = useCloudStore()
// define props
const props = defineProps({
  index: {
    type: Number,
    required: true
  },
  article: {
    type: Object,
    required: true
  }
})
// define emits
const emit = defineEmits(['show', 'remove'])
// define reactive props
const addPrintModalActive = ref(false)
const dropzoneFile = ref('')
const printForm = reactive(new Form({
  print: null,
  file: null,
  count: 1
}))
const uploadForm = reactive(new FormDataForm({
  file: null,
}))
// define computed properties
const locale = computed(() => store.settings.locale)
const cloud = computed(() => store.tmp.cloud)
const exhibition = computed(() => store.tmp.exhibition_slug)
const basket = computed(() => store.print.basket)
const isShopClosed = computed(() => store.tmp.is_exhibition_closed)

const inEnglish = computed(() => {
  return locale.value === 'en'
})

const showCount = computed(() => {
  if (printForm.print) {
    const printArticle = availablePrints.value.find((item) => item.article_id === printForm.print.article_id)
    console.log('printArticle', printArticle);
    if (printArticle && printArticle.unit === 'sqm') {
      printForm.count = props.article.amount
      return true
    }
  }
  printForm.count = 1
  return false
})


const isCloudStatusOpen = computed(() => {
  return cloud.value.status === PRINT_CLOUD_STATUS_OPEN
})

const dropzoneId = computed(() => {
  return 'dropzone_group_' + props.article.id + '_' + props.index
})

const baseArticle = computed(() => {
  return props.article.article
})

const name = computed(() => {
  return (inEnglish.value) ? baseArticle.value.name_en : baseArticle.value.name
})

const availablePrints = computed(() => {
  return cloudStore.getAvailableArticleRefinements(props.article, configurations.value, props.index)
  // const prints = props.article.prints
  //   .filter((print) => store.availability.available_article_ids.includes(print.article_id))
  //   .filter((print) => store.availability.bookable_article_ids.includes(print.article_id))
  // if (configurations.value.length === 0 && basket.value.print_configurations.length === 0) {
  //   return prints
  // }

  // const paidArticleIds = configurations.value.map((configuration) => configuration.request_article_id)
  // const basketArticleIds = basket.value.print_configurations
  //   .filter((configuration) => {
  //     return (
  //       configuration.group === props.index
  //       && configuration.parent_id === props.article.article_id
  //     )
  //   })
  //   .map((configuration) => configuration.id)
  // return prints.filter((item) => {
  //   const config = item.configurations
  //     .filter((configuration) => configuration.group === props.index)

  //   if (config.length === 1) {
  //     return !(paidArticleIds.includes(config[0].request_article_id) || basketArticleIds.includes(config[0].request_article_id))
  //   }

  //   return true;
  // })
})

const missingPrints = computed(() => {
  return props.article.prints
    .filter((print) => !store.availability.available_article_ids.includes(print.article_id))
})

const notBookablePrints = computed(() => {
  return props.article.prints
    .filter((print) => !store.availability.bookable_article_ids.includes(print.article_id))
})

const showOrderWarning = computed(() => {
  return (
    availablePrints.value.length === 0
    && (missingPrints.value.length > 0 || notBookablePrints.value.length > 0)
  )
})

const configurations = computed(() => {
  return props.article.prints
    .map((item) => item.configurations)
    .flat()
    .filter((configuration) => {
      return configuration.group === props.index
    })
})

const basketConfigurations = computed(() => {
  return basket.value.print_configurations.filter((configuration) => {
    return (
      configuration.group === props.index
      && configuration.parent_id === props.article.article_id
    )
  })
})
// define methods
const show = (article) => {
  emit('show', article)
}
const showAddPrintModal = () => {
  addPrintModalActive.value = true
}

const hideAddPrintModal = () => {
  addPrintModalActive.value = false
}

const drop = (e) => {
  dropzoneFile.value = e.dataTransfer.files[0]
}

const selectedFile = () => {
  dropzoneFile.value = document.getElementById(dropzoneId.value).files[0]
}

const upload = async () => {
  showLoadingIndicator(t('print_cloud.indicator.upload'))
  uploadForm.file = dropzoneFile.value
  await uploadForm.post('/' + exhibition.value + '/upload')
    .then(response => {
      uploadForm.resetToOriginal()
      printForm.file = response.data.file
      hideLoadingIndicator()
    })
    .catch(err => {
      if (err.status === 413) {
        showWarningToast(t('toast.warnings.too_large'), t('toast.title.warning'), 5000)
      } else {
        showErrorToast(err.message, t('toast.title.error'))
      }
    })
}

const addConfiguration = async () => {
  printForm.errors.clear()
  if (printForm.print === null) {
    printForm.errors.record({
      errors: {
        'print': t('configurator.form.errors.graphic_design')
      }
    })

    return
  }

  if (dropzoneFile.value !== '') {
    await upload()
  }

  // check if article is in basket
  if (basket.value.hasArticlePrintItem(printForm.print)) {
    basket.value.increaseItemCountForArticlePrint(printForm.print, printForm.count)
  } else {
    basket.value.addItemForArticlePrint(printForm.print, printForm.count)
  }

  // add print configuration to basket
  basket.value.addPrintConfiguration({
    id: printForm.print.id,
    group: props.index,
    article_id: printForm.print.article_id,
    parent_id: printForm.print.parent_id,
    article_print_id: printForm.print.article_print_id,
    count: printForm.count,
    file: printForm.file,
  })

  printForm.resetToOriginal()
  dropzoneFile.value = ''
  hideAddPrintModal()
}

const removeConfiguration = (configuration) => {
  const print = props.article.prints.find((item) => {
    return item.id === configuration.id
  })

  if (print) {
    // remove configuration
    basket.value.removePrintConfiguration(configuration)
    // decrease item count
    basket.value.decreaseItemCountForArticlePrint(print, 1)
  }
}
</script>